import { KendraConst, SearchInterface, AdminPreferences } from "@amzn/ask-legal-domain";
import { Multiselect, MultiselectProps } from "@amzn/awsui-components-react";
import * as React from "react";
import { UIModel } from "../../../model/ui-model";
import { useInstanceDataContext } from "../../../contexts/InstanceDataContext";
import { getStringFilterValues, replaceStringFilters } from "./search-filter-utils-v2";

interface InstanceFilterV2Props {
    instanceIds: string[];
    queryInputState: UIModel.State<SearchInterface.SearchQueryInput>;
    disabled?: boolean;
}

const INSTANCE_ID = KendraConst.ContainerIngestionKendraAttributes.Default.InstanceId;

/**
 * Teams filter. Nothing selected (or every team selected) means no instance filter, i.e. all teams.
 * Organization groups select or clear all of their teams at once.
 */
export const InstanceFilterV2: React.FC<InstanceFilterV2Props> = ({ instanceIds, queryInputState, disabled }) => {
    const { instanceCache, getCurrentInstanceId, loadInstance } = useInstanceDataContext();
    const currentInstanceId = getCurrentInstanceId();

    // Load display names once per set of ids (a failed load is not retried on every cache update)
    React.useEffect(() => {
        instanceIds.forEach(instanceId => {
            if (!instanceCache[instanceId]) {
                loadInstance(instanceId).catch(console.error);
            }
        });
    }, [instanceIds, loadInstance]);

    const optionsById = React.useMemo(() => {
        const map = new Map<string, MultiselectProps.Option>();
        instanceIds.forEach(id => {
            const name = instanceCache[id]?.name || id;
            map.set(id, { label: id === currentInstanceId ? `${name} (Current)` : name, value: id });
        });
        return map;
    }, [instanceIds, instanceCache, currentInstanceId]);

    const options = React.useMemo<MultiselectProps.Options>(() => {
        const byName = (a: string, b: string) =>
            (optionsById.get(a)?.label || a).localeCompare(optionsById.get(b)?.label || b, undefined, { sensitivity: "base" });
        // Current team first, then alphabetical
        const sorted = [...instanceIds].sort((a, b) =>
            a === currentInstanceId ? -1 : b === currentInstanceId ? 1 : byName(a, b)
        );
        const awsLegal: MultiselectProps.Option[] = [];
        const amazonLegal: MultiselectProps.Option[] = [];
        const other: MultiselectProps.Option[] = [];
        sorted.forEach(id => {
            const option = optionsById.get(id)!;
            const organization = instanceCache[id]?.preferences?.organization;
            if (organization === AdminPreferences.Organization.AWS_LEGAL) awsLegal.push(option);
            else if (organization === AdminPreferences.Organization.AMAZON_LEGAL) amazonLegal.push(option);
            else other.push(option);
        });
        return [
            ...(awsLegal.length ? [{ label: "AWS Legal", options: awsLegal }] : []),
            ...(amazonLegal.length ? [{ label: "Amazon Legal", options: amazonLegal }] : []),
            ...other
        ];
    }, [instanceIds, instanceCache, currentInstanceId, optionsById]);

    const selectedOptions = getStringFilterValues(queryInputState.value, INSTANCE_ID)
        .map(id => optionsById.get(id) || { label: id, value: id });

    const onChange = (selected: ReadonlyArray<MultiselectProps.Option>) => {
        const ids = selected.map(o => o.value).filter((v): v is string => !!v);
        // Every team selected is the same as no filter
        const filterIds = ids.length >= instanceIds.length ? [] : ids;
        queryInputState.setValue(replaceStringFilters(queryInputState.value, INSTANCE_ID, filterIds));
    };

    return (
        <Multiselect
            inlineLabelText="Teams"
            placeholder="All teams"
            filteringType="auto"
            inlineTokens
            selectedOptions={selectedOptions}
            options={options}
            disabled={disabled}
            onChange={({ detail }) => onChange(detail.selectedOptions)}
        />
    );
};
